import { APPLICATION_PHASES } from '@paguz/contracts';
import {
  Badge,
  cn,
  MoneyText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@paguz/ui';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  ArrowLeftIcon,
  BotIcon,
  CheckCircle2Icon,
  CircleIcon,
  ShieldCheckIcon,
  UserIcon,
} from 'lucide-react';
import { APPLICATION_TABS, type ApplicationTab } from '../../app/search.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import type { AdminApplicationDetail } from '../../api/origination-schemas.ts';
import {
  CopyValue,
  DataList,
  ErrorState,
  LoadingRows,
  PageHeader,
  Panel,
} from '../../components/page.tsx';
import { formatDateTime, formatShortDateTime } from '../../lib/format.ts';
import { APPLICATION_STATUS, CONSENT_LABEL, PHASE_LABEL } from '../../lib/labels.ts';
import { slaFor, useConsoleNow } from '../../lib/sla.ts';
import { CashFlowTab } from './cash-flow-tab.tsx';
import { DecisionPanel } from './decision-panel.tsx';
import { DocumentsTab } from './documents-tab.tsx';
import { ROUTE_LABEL } from './labels.ts';
import { RecommendationTab } from './recommendation-tab.tsx';
import { RiskTab } from './risk-tab.tsx';

const route = getRouteApi('/app/expedientes/$applicationId');

const TAB_LABEL: Readonly<Record<ApplicationTab, string>> = {
  resumen: 'Resumen',
  documentos: 'Documentos',
  flujo: 'Flujo de caja',
  riesgo: 'Riesgo',
  recomendacion: 'Recomendación',
  conversaciones: 'Conversaciones',
  historial: 'Historial',
};

export function ApplicationPage() {
  const { applicationId } = route.useParams();
  const query = useQuery({
    queryKey: queryKeys.application(applicationId),
    queryFn: () => api.getApplication(applicationId),
  });
  return (
    <>
      <Link
        to="/expedientes"
        className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
      >
        <ArrowLeftIcon className="size-4" /> Expedientes
      </Link>
      {query.isPending ? (
        <LoadingRows rows={10} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <ApplicationDetailView detail={query.data} />
      )}
    </>
  );
}

function ApplicationDetailView({ detail }: { detail: AdminApplicationDetail }) {
  const { application: app } = detail;
  const { tab = 'resumen' } = route.useSearch();
  const navigate = route.useNavigate();
  const now = useConsoleNow();
  const sla = slaFor(detail.phaseEnteredAt, now);
  const tabCount: Partial<Record<ApplicationTab, number>> = {
    documentos: app.documents.length,
    conversaciones: detail.conversations.length,
  };

  return (
    <>
      <PageHeader
        eyebrow={<CopyValue value={app.id} label="ID del expediente" />}
        title={detail.applicant.fullName}
        description={`${app.businessProfile.businessName ?? 'Negocio sin nombre'} · ${app.businessProfile.activityDescription ?? ''} · ${detail.applicant.city ?? ''}`}
        actions={
          <>
            <Badge variant={APPLICATION_STATUS[app.status].tone} className="text-sm">
              {APPLICATION_STATUS[app.status].label}
            </Badge>
            {detail.policyRoute && (
              <Badge variant="outline" className="text-sm">
                Ruta: {ROUTE_LABEL[detail.policyRoute.route]}
              </Badge>
            )}
            {app.status === 'SUBMITTED' && (
              <Badge variant={sla.tone} className="text-sm">
                SLA: {sla.label} en fase
              </Badge>
            )}
          </>
        }
      />

      <PhaseTimeline detail={detail} />

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
        <Tabs
          value={tab}
          onValueChange={(value) => {
            void navigate({ search: { tab: value as ApplicationTab } });
          }}
          className="min-w-0"
        >
          <TabsList variant="line" className="flex-wrap gap-x-3 gap-y-0">
            {APPLICATION_TABS.map((t) => (
              <TabsTrigger key={t} value={t}>
                {TAB_LABEL[t]}
                {tabCount[t] !== undefined && (
                  <span className="rounded-full bg-muted px-1.5 text-xs text-muted-foreground">
                    {tabCount[t]}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="resumen">
            <SummaryTab detail={detail} />
          </TabsContent>
          <TabsContent value="documentos">
            <DocumentsTab detail={detail} />
          </TabsContent>
          <TabsContent value="flujo">
            <CashFlowTab detail={detail} />
          </TabsContent>
          <TabsContent value="riesgo">
            <RiskTab detail={detail} />
          </TabsContent>
          <TabsContent value="recomendacion">
            <RecommendationTab detail={detail} />
          </TabsContent>
          <TabsContent value="conversaciones">
            <ConversationsTab detail={detail} />
          </TabsContent>
          <TabsContent value="historial">
            <HistoryTab detail={detail} />
          </TabsContent>
        </Tabs>
        <aside className="xl:sticky xl:top-24 xl:self-start">
          <DecisionPanel detail={detail} />
        </aside>
      </div>
    </>
  );
}

const TIMELINE = APPLICATION_PHASES.filter((p) => p !== 'ACTIVE');

function PhaseTimeline({ detail }: { detail: AdminApplicationDetail }) {
  const current = TIMELINE.indexOf(detail.application.phase as (typeof TIMELINE)[number]);
  const entered = new Map(detail.phaseHistory.map((h) => [h.to, h.at]));
  return (
    <Panel bodyClassName="px-5 py-4">
      <ol className="flex items-start gap-1 overflow-x-auto" aria-label="Fases del expediente">
        {TIMELINE.map((phase, index) => {
          const done = index < current;
          const active = index === current;
          const at = entered.get(phase) ?? (index === 0 ? detail.application.createdAt : undefined);
          return (
            <li key={phase} className="flex min-w-24 flex-1 flex-col items-center text-center">
              <div className="flex w-full items-center">
                <span
                  className={cn(
                    'h-0.5 flex-1',
                    index === 0 ? 'opacity-0' : done || active ? 'bg-highlight' : 'bg-border',
                  )}
                />
                {done ? (
                  <CheckCircle2Icon className="size-5 shrink-0 text-highlight" />
                ) : (
                  <CircleIcon
                    className={cn(
                      'size-5 shrink-0',
                      active ? 'fill-highlight text-highlight' : 'text-border',
                    )}
                  />
                )}
                <span
                  className={cn(
                    'h-0.5 flex-1',
                    index === TIMELINE.length - 1
                      ? 'opacity-0'
                      : done
                        ? 'bg-highlight'
                        : 'bg-border',
                  )}
                />
              </div>
              <span
                aria-current={active ? 'step' : undefined}
                className={cn(
                  'mt-1.5 text-xs font-semibold',
                  active ? 'text-primary' : done ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {PHASE_LABEL[phase]}
              </span>
              {(done || active) && at && (
                <span className="text-[11px] whitespace-nowrap text-muted-foreground">
                  {formatShortDateTime(at)}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </Panel>
  );
}

function SummaryTab({ detail }: { detail: AdminApplicationDetail }) {
  const { application: app, applicant } = detail;
  const biz = app.businessProfile;
  const home = app.householdProfile;
  return (
    <div className="flex flex-col gap-6">
      <Panel title="Solicitante y negocio">
        <DataList
          columns={3}
          items={[
            { label: 'Nombre', value: applicant.fullName },
            { label: 'Documento', value: applicant.maskedDocument },
            { label: 'Celular', value: applicant.maskedPhone },
            { label: 'Edad', value: applicant.age ? `${applicant.age} años` : '—' },
            {
              label: 'Cliente',
              value: (
                <Link
                  to="/clientes/$customerId"
                  params={{ customerId: applicant.customerId }}
                  className="font-semibold text-primary hover:underline"
                >
                  Ver ficha
                </Link>
              ),
            },
            {
              label: 'Monto solicitado',
              value: app.requestedAmount ? (
                <MoneyText value={app.requestedAmount.amount} size="sm" />
              ) : (
                '—'
              ),
              hint: app.purpose ?? undefined,
            },
            { label: 'Negocio', value: biz.businessName ?? '—', hint: biz.activityDescription },
            {
              label: 'Antigüedad',
              value:
                biz.businessAgeMonths !== undefined
                  ? `${Math.floor(biz.businessAgeMonths / 12)} años (${biz.businessAgeMonths} meses)`
                  : '—',
            },
            {
              label: 'Ubicación',
              value: biz.address ?? applicant.city ?? '—',
              hint: biz.municipalityCode ? `DANE ${biz.municipalityCode}` : undefined,
            },
            {
              label: 'Local',
              value: {
                OWNED: 'Propio',
                RENTED: 'Arrendado',
                HOME_BASED: 'En la casa',
                MOBILE: 'Ambulante',
              }[biz.premises ?? 'RENTED'],
            },
            { label: 'Empleados', value: biz.employees ?? '—' },
            { label: 'Canales de venta', value: biz.salesChannels?.join(', ') ?? '—' },
            {
              label: 'Hogar',
              value: home.householdSize
                ? `${home.householdSize} personas (${home.dependents ?? 0} a cargo)`
                : '—',
            },
            { label: 'Completitud', value: `${app.completeness.replace('.', ',')} %` },
            { label: 'Canal', value: 'Asesor IA (web)' },
          ]}
        />
      </Panel>
      <Panel title="Consentimientos" description="Con fecha, versión del texto y evidencia (OTP).">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Consentimiento</TableHead>
              <TableHead>Versión</TableHead>
              <TableHead>Otorgado</TableHead>
              <TableHead>Evidencia</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {detail.consents.map((c) => (
              <TableRow key={c.type}>
                <TableCell className="font-medium">
                  <ShieldCheckIcon className="mr-1.5 inline size-4 text-success" />
                  {CONSENT_LABEL[c.type] ?? c.type}
                </TableCell>
                <TableCell>{c.version}</TableCell>
                <TableCell>{formatDateTime(c.grantedAt)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {c.evidence.channel} · OTP {c.evidence.otpChallengeId ? '✓' : '—'}
                  {c.revokedAt && (
                    <Badge variant="danger" className="ml-2">
                      Revocado
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
      <Panel title="Lista de requisitos">
        <ul className="flex flex-col gap-2">
          {app.checklist.map((c) => (
            <li key={c.code} className="flex items-center justify-between gap-3 text-sm">
              <span>
                {c.label}{' '}
                {c.required && <span className="text-muted-foreground">· obligatorio</span>}
              </span>
              <Badge
                variant={
                  c.status === 'DONE' ? 'success' : c.status === 'PENDING' ? 'warning' : 'secondary'
                }
              >
                {
                  {
                    DONE: 'Listo',
                    PENDING: 'Pendiente',
                    IN_REVIEW: 'En revisión',
                    NOT_APPLICABLE: 'No aplica',
                  }[c.status]
                }
              </Badge>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

function ConversationsTab({ detail }: { detail: AdminApplicationDetail }) {
  if (detail.conversations.length === 0)
    return (
      <Panel>
        <p className="text-sm text-muted-foreground">Sin conversaciones registradas.</p>
      </Panel>
    );
  return (
    <div className="flex flex-col gap-6">
      {detail.conversations.map((c) => (
        <Panel
          key={c.sessionId}
          title={`Asesor IA · ${formatDateTime(c.startedAt)}`}
          description={`${c.turns} turnos · costo USD ${c.costUsd}`}
          actions={
            <Link
              to="/agentes/sesiones/$sessionId"
              params={{ sessionId: c.sessionId }}
              className="text-sm font-semibold text-primary hover:underline"
            >
              Ver traza completa
            </Link>
          }
        >
          <ol className="flex flex-col gap-3">
            {c.excerpt.map((m) => (
              <li
                key={m.seq}
                className={cn('flex gap-3', m.role === 'user' && 'flex-row-reverse text-right')}
              >
                <span className="mt-1 shrink-0 text-muted-foreground">
                  {m.role === 'user' ? (
                    <UserIcon className="size-4" />
                  ) : (
                    <BotIcon className="size-4" />
                  )}
                </span>
                <div
                  className={cn(
                    'max-w-[75%] rounded-2xl px-4 py-2.5 text-sm',
                    m.role === 'user'
                      ? 'bg-highlight-soft text-highlight-soft-foreground'
                      : 'bg-muted',
                  )}
                >
                  <p>{m.text}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Turno {m.seq} · {formatDateTime(m.at)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </Panel>
      ))}
    </div>
  );
}

function HistoryTab({ detail }: { detail: AdminApplicationDetail }) {
  const events = [...detail.history].sort((a, b) => b.at.localeCompare(a.at));
  return (
    <Panel
      title="Historial del expediente"
      description="Eventos y auditoría (más reciente primero)."
    >
      <ol className="flex flex-col gap-4">
        {events.map((e, i) => (
          <li key={`${e.at}-${i}`} className="grid grid-cols-[9rem_minmax(0,1fr)] gap-4 text-sm">
            <span className="text-muted-foreground">{formatDateTime(e.at)}</span>
            <div>
              <p className="font-semibold text-foreground">{e.detail}</p>
              <p className="text-xs text-muted-foreground">
                {e.actor.name} · <code className="font-mono">{e.action}</code>
              </p>
            </div>
          </li>
        ))}
      </ol>
    </Panel>
  );
}
